import { Injectable } from '@angular/core';

import { AppInfo, Config, HttpMethod, HttpMethodRequest, HttpResponse } from '@mitel/cloudlink-sdk';
import { environment } from './../../../environments/environment';
import { EntitiesCollection, PlatformEmbedded, ComponentEmbedded, LocationEmbedded } from '../interfaces/collections';
import * as _ from 'lodash';


// Copied from system inventory spa. Only 1 method is used now. Should I remove the others or leave them here to be
// used by other features in the future?

export interface QueryParam {
  $top?: number;
  $skip?: number;
  $skipToken?: string;
  $filter?: string;
  $expand?: string;
  $context?: string;
}
@Injectable({
  providedIn: 'root'
})
export class SystemManagerService {
  private systemManagerService: any;

  constructor() {
    this.systemManagerService = {
      baseUrl: environment.systemManagerUrl,
      httpService: <HttpMethod>Config.httpService,
      appConfig: <AppInfo>Config.appInfo,

      async getPlatforms(queryParams?: QueryParam, skipToken?: string): Promise<EntitiesCollection<PlatformEmbedded>> {
        let url = `${this.baseUrl}/platforms`;
        let headers = Object.assign(this.appConfig?.getAppHeaders());
        if (skipToken) {
          // rootUrl: 'https://system-manager.dev.api.mitel.io/2023-07-01'
          // skipToken: starts with /2023-07-01
          let lastSlash = this.baseUrl.lastIndexOf('/');
          url = this.baseUrl.substring(0, lastSlash) + skipToken;
        }
        else {
          if (queryParams) {
            url = this.addQueryParamsToUrl(url, queryParams);
          }
        }
        let httpRequest: HttpMethodRequest = {
          url: url,
          headers: headers
        }

        try {
          let getPlatformsResponse: HttpResponse = await this.httpService.get(httpRequest);
          return getPlatformsResponse.body;
        } catch (error) {
          throw error;
        }
      },
      async getPlatform(platformId: string, queryParams?: QueryParam, token?: any): Promise<PlatformEmbedded> {
        let url = `${this.baseUrl}/platforms/${platformId}`;
        let headers = Object.assign(this.appConfig?.getAppHeaders(), { ...token});
        if (queryParams) {
          url = this.addQueryParamsToUrl(url, queryParams);
        }
        let httpRequest: HttpMethodRequest = {
          url: url,
          headers: headers
        }
        try {
          let getPlatformResponse: HttpResponse = await this.httpService.get(httpRequest);
          return getPlatformResponse.body;
        } catch (error) {
          throw error;
        }
      },
      async getPlatformLaunchUrl(platformId: string, serviceId: string, queryParams?: QueryParam, token?: any): Promise<LocationEmbedded> {
        let url = `${this.baseUrl}/platforms/${platformId}/services/${serviceId}/url`;
        
        let headers = Object.assign(this.appConfig?.getAppHeaders(), { ...token});
        if (queryParams) {
          url = this.addQueryParamsToUrl(url, queryParams);
        }
        let httpRequest: HttpMethodRequest = {
          url: url,
          headers: headers
        }
        try {
          let getPlatformLaunchUrlResponse: HttpResponse = await this.httpService.get(httpRequest);
          return getPlatformLaunchUrlResponse.body;
        } catch (error) {
          throw error;
        }
      },
      async getComponents(queryParams?: QueryParam, skipToken?: string): Promise<EntitiesCollection<ComponentEmbedded>> {
        let url = `${this.baseUrl}/components`;
        let headers = Object.assign(this.appConfig?.getAppHeaders());
        if (skipToken) {
          // rootUrl: 'https://system-manager.dev.api.mitel.io/2023-07-01'
          // skipToken: starts with /2023-07-01
          let lastSlash = this.baseUrl.lastIndexOf('/');
          url = this.baseUrl.substring(0, lastSlash) + skipToken;
        }
        else {
          if (queryParams) {
            url = this.addQueryParamsToUrl(url, queryParams);
          }
        }
        let httpRequest: HttpMethodRequest = {
          url: url,
          headers: headers
        }

        try {
          let getComponentsResponse: HttpResponse = await this.httpService.get(httpRequest);
          return getComponentsResponse.body;
        } catch (error) {
          throw error;
        }
      },
      async getComponent(componentId: string, queryParams?: QueryParam, token?: any): Promise<ComponentEmbedded> {
        let url = `${this.baseUrl}/components/${componentId}`;
        let headers = Object.assign(this.appConfig?.getAppHeaders(), { ...token});
        if (queryParams) {
          url = this.addQueryParamsToUrl(url, queryParams);
        }
        let httpRequest: HttpMethodRequest = {
          url: url,
          headers: headers
        }
        try {
          let getComponentResponse: HttpResponse = await this.httpService.get(httpRequest);
          return getComponentResponse.body;
        } catch (error) {
          throw error;
        }
      },
      async getComponentLaunchUrl(componentId: string, serviceId: string, queryParams?: QueryParam, token?: any): Promise<LocationEmbedded> {
        let url = `${this.baseUrl}/components/${componentId}/services/${serviceId}/url`;
        
        let headers = Object.assign(this.appConfig?.getAppHeaders(), { ...token});
        if (queryParams) {
          url = this.addQueryParamsToUrl(url, queryParams);
        }
        let httpRequest: HttpMethodRequest = {
          url: url,
          headers: headers
        }
        try {
          let getComponentLaunchUrlResponse: HttpResponse = await this.httpService.get(httpRequest);
          return getComponentLaunchUrlResponse.body;
        } catch (error) {
          throw error;
        }
      },
      addQueryParamsToUrl(url: string, queryParams: QueryParam): string {
        let firstParam: boolean = true;

        _.forEach(queryParams, (value, qParam) => {
          if (firstParam) {
            url = `${url}?`;
            firstParam = false;
          } else {
            url = `${url}&`;
          }
          url = `${url}${qParam}=${value}`;
        });
        return url;
      }
    }
  }

  public async getPlatforms(queryParams?: QueryParam, skipToken?: string): Promise<PlatformEmbedded[]> {
    return new Promise<PlatformEmbedded[]>(async (resolve, reject) => {
      let platforms: PlatformEmbedded[] = [];
      try {
        queryParams = {...queryParams, $top: 20};
        const platformCollection = await this.systemManagerService.getPlatforms(queryParams, skipToken);
        if (platformCollection && platformCollection.count > 0 && platformCollection._embedded && platformCollection._embedded.items) {
          platforms = platformCollection._embedded.items;
          if (platformCollection._links && platformCollection._links.next) {
            const morePlatforms = await this.getPlatforms(queryParams, platformCollection._links.next);
            if (morePlatforms) {
              platforms = platforms.concat(morePlatforms);
            }
          }
        }
        resolve(platforms);
      } catch (error) {
        reject(error);
      }
    });
  }

  public async getPlatform(platformId: string, queryParams?: QueryParam, token?: any): Promise<PlatformEmbedded> {
    let getPlatformResponse: PlatformEmbedded = await this.systemManagerService.getPlatform(platformId, queryParams, token);
    return getPlatformResponse;
  }

  public async getPlatformLaunchUrl(platformId: string, serviceId: string, queryParams?: QueryParam, token?: any): Promise<LocationEmbedded> {
    let getPlatformLaunchUrlResponse: LocationEmbedded = await this.systemManagerService.getPlatformLaunchUrl(platformId, serviceId, queryParams, token);
    return getPlatformLaunchUrlResponse;
  }

  public async getComponents(queryParams?: QueryParam, skipToken?: string): Promise<ComponentEmbedded[]> {

    return new Promise<ComponentEmbedded[]>(async (resolve, reject) => {
      let components: ComponentEmbedded[] = [];
      try {
        queryParams = {...queryParams, $top: 20};
        const componentCollection = await this.systemManagerService.getComponents(queryParams, skipToken);
        if (componentCollection && componentCollection.count > 0 && componentCollection._embedded && componentCollection._embedded.items) {
          components = componentCollection._embedded.items;
          if (componentCollection._links && componentCollection._links.next) {
            const moreComponents = await this.getComponents(queryParams, componentCollection._links.next);
            if (moreComponents) {
              components = components.concat(moreComponents);
            }
          }
        }
        resolve(components);
      } catch (error) {
        reject(error);
      }
    });
  }

  public async getComponent(componentId: string, queryParams?: QueryParam, token?: any): Promise<ComponentEmbedded> {
    let getComponentResponse: ComponentEmbedded = await this.systemManagerService.getComponent(componentId, queryParams, token);
    return getComponentResponse;
  }

  public async getComponentLaunchUrl(componentId: string, serviceId: string, queryParams?: QueryParam, token?: any): Promise<LocationEmbedded> {
    let getComponentLaunchUrlResponse: LocationEmbedded = await this.systemManagerService.getComponentLaunchUrl(componentId, serviceId, queryParams, token);
    return getComponentLaunchUrlResponse;
  }
}